import classnames from 'classnames/bind'
import {
  ComponentType,
  forwardRef,
  ForwardRefRenderFunction,
  ReactChild,
  useImperativeHandle,
  useRef,
  useTransition,
} from 'react'

import { CloseIcon } from '~/components/Abstracts/Icons'

import { usePopIn } from '~/providers/PopInProvider'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface PopInCloseIconProps {
  className?: string
  theme?: 'dark' | 'light'
  onClick?(): void
  as?: ComponentType<PopInCloseIconProps>
}

export const PopInCloseIcon = ({
  className,
  theme = 'dark',
  as,
  onClick,
}: PopInCloseIconProps) => {
  const { removeCurrent } = usePopIn()
  const [isPending, startTransition] = useTransition()

  const processedOnClick = () => {
    startTransition(() => {
      removeCurrent()
      onClick?.()
    })
  }

  const Component = as ?? CloseIcon

  return (
    <button
      className={className}
      type="button"
      onClick={processedOnClick}
      aria-label="Close">
      <Component
        className={cx(css.closeIcon, css[theme])}
        style={{
          opacity: isPending ? 0.5 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
    </button>
  )
}

export interface PopInProps {
  className?: string
  children?: ReactChild[] | ReactChild
  isCloseButton?: boolean
  closeButton?: PopInCloseIconProps['as']
  closeClassName?: string
  theme?: 'dark' | 'light'
  onClose?(): void
}

const PopIn: ForwardRefRenderFunction<HTMLDivElement, PopInProps> = (
  {
    className,
    children,
    isCloseButton = true,
    closeButton,
    closeClassName,
    theme,
    onClose,
  },
  ref,
) => {
  const popInRef = useRef(null)

  useImperativeHandle(ref, () => popInRef.current)

  return (
    <div ref={popInRef} className={className} role="dialog" aria-modal="true">
      {isCloseButton && (
        <PopInCloseIcon
          className={closeClassName}
          theme={theme}
          as={closeButton}
          onClick={onClose}
        />
      )}
      {children}
    </div>
  )
}

export default forwardRef<HTMLDivElement, PopInProps>(PopIn)
